import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/react';
import { AppShell } from '@/components/AppShell';
import { useBusinesses } from '@/context/BusinessContext';
import {
  ApiError,
  createTransaction,
  getAccounts,
  getTaxCodes,
  getTransactions,
  type Account,
  type PaymentMethod,
  type TaxCode,
  type Transaction,
  type TransactionEntry,
  type TransactionHeader,
} from '@/lib/api';

// ---- money / date helpers -------------------------------------------------

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Parse a dollars string (e.g. "100", "100.5", "100.50") to integer cents. */
function parseDollarsToCents(input: string): number | null {
  const s = input.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const [whole, frac = ''] = s.split('.');
  return Number(whole) * 100 + Number((frac + '00').slice(0, 2));
}

/** Today's calendar date in SI-local time (UTC+11), to match the reference clock. */
function todayISO(): string {
  return new Date(Date.now() + 11 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

const PAYMENT_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK', label: 'From a bank account' },
  { value: 'CREDIT', label: 'On credit (we owe them)' },
];

/**
 * EXPENSE-category accounts that are not direct supplier payments — they post
 * from other modules, so the Expense card doesn't offer them: 6100 Salaries and
 * 6110 SINPF (payroll, Pass 11), 6700 Depreciation and 6900 Income Tax Expense
 * (General / Adjustments). Mirrors the rationale in PROJECT_CONTEXT. NOTE: this
 * is a frontend guard only — the engine still accepts any cost account.
 */
const EXCLUDED_EXPENSE_CODES = ['6100', '6110', '6700', '6900'];

const inputClass =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 ' +
  'placeholder-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100';
const labelClass = 'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300';
const cardClass =
  'rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900';

function isCostAccount(a: Account): boolean {
  return (
    (a.category === 'COGS' || a.category === 'EXPENSE') &&
    !EXCLUDED_EXPENSE_CODES.includes(a.code)
  );
}

// ---- component ------------------------------------------------------------

export function Expenses() {
  const { getToken } = useAuth();
  const { activeBusinessId, activeBusiness } = useBusinesses();
  const currency = activeBusiness?.currency_code ?? 'SBD';

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(true);
  const [refsError, setRefsError] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(true);

  // form state
  const [expenseAccountId, setExpenseAccountId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('CASH');
  const [settlementAccountId, setSettlementAccountId] = useState('');
  const [whtCodeId, setWhtCodeId] = useState('');
  const [reference, setReference] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [txnDate, setTxnDate] = useState(todayISO());

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{
    transaction: TransactionHeader;
    entries: TransactionEntry[];
  } | null>(null);

  const cashAccounts = useMemo(
    () => accounts.filter((a) => a.cash_bank_kind === 'CASH'),
    [accounts],
  );
  const bankAccounts = useMemo(
    () => accounts.filter((a) => a.cash_bank_kind === 'BANK'),
    [accounts],
  );

  // Expense-account picker, split into the two cost groups the P&L reports on.
  const cogsAccounts = useMemo(
    () => accounts.filter((a) => a.category === 'COGS' && !EXCLUDED_EXPENSE_CODES.includes(a.code)),
    [accounts],
  );
  const opexAccounts = useMemo(
    () => accounts.filter((a) => a.category === 'EXPENSE' && !EXCLUDED_EXPENSE_CODES.includes(a.code)),
    [accounts],
  );
  const hasExpenseAccounts = cogsAccounts.length + opexAccounts.length > 0;

  // Withholding codes (Expense module only), grouped resident vs non-resident.
  const whtResident = useMemo(
    () => taxCodes.filter((t) => t.tax_type === 'WITHHOLDING' && !t.code.startsWith('WHT-NR-')),
    [taxCodes],
  );
  const whtNonResident = useMemo(
    () => taxCodes.filter((t) => t.tax_type === 'WITHHOLDING' && t.code.startsWith('WHT-NR-')),
    [taxCodes],
  );
  const hasWht = whtResident.length + whtNonResident.length > 0;

  const selectedWht = useMemo(
    () => taxCodes.find((t) => t.id === whtCodeId) ?? null,
    [taxCodes, whtCodeId],
  );
  // WHT applies only to immediate (CASH/BANK) payments; a CREDIT bill carries none.
  const effectiveWht = paymentMethod === 'CREDIT' ? null : selectedWht;

  const netCents = parseDollarsToCents(amount);
  // Net-entry gross-up, matching the engine: WHT = round(net × bps ÷ (10000 − bps)).
  const whtBps = effectiveWht?.rate_basis_points ?? 0;
  const whtCents =
    effectiveWht && netCents !== null && whtBps > 0 && whtBps < 10000
      ? Math.round((netCents * whtBps) / (10000 - whtBps))
      : 0;
  const grossCents = (netCents ?? 0) + whtCents;

  const accountLabel = useCallback(
    (id: string) => {
      const a = accounts.find((x) => x.id === id);
      return a ? `${a.code} ${a.name}` : id;
    },
    [accounts],
  );

  const loadRefs = useCallback(async () => {
    if (!activeBusinessId) return;
    setLoadingRefs(true);
    setRefsError(null);
    try {
      const token = await getToken();
      const [accs, codes] = await Promise.all([
        getAccounts(token, activeBusinessId),
        getTaxCodes(token, activeBusinessId),
      ]);
      setAccounts(accs);
      setTaxCodes(codes);

      // Default to the first eligible cost account (COGS first, then OpEx).
      const eligible = accs.filter(isCostAccount);
      setExpenseAccountId(eligible[0]?.id ?? '');
      // Settlement account is chosen by the keep-valid effect below.
    } catch (err) {
      setRefsError(
        err instanceof ApiError ? err.message : 'Could not load the chart of accounts.',
      );
    } finally {
      setLoadingRefs(false);
    }
  }, [activeBusinessId, getToken]);

  const loadTransactions = useCallback(async () => {
    if (!activeBusinessId) return;
    setLoadingList(true);
    try {
      const token = await getToken();
      setTransactions(await getTransactions(token, activeBusinessId, 'EXPENSE'));
    } catch {
      // A failed list refresh is non-fatal; leave the prior list in place.
    } finally {
      setLoadingList(false);
    }
  }, [activeBusinessId, getToken]);

  useEffect(() => {
    void loadRefs();
    void loadTransactions();
  }, [loadRefs, loadTransactions]);

  // Keep the chosen settlement account consistent with the payment method.
  useEffect(() => {
    if (paymentMethod === 'CREDIT') return;
    const opts = paymentMethod === 'CASH' ? cashAccounts : bankAccounts;
    if (opts.length > 0 && !opts.some((a) => a.id === settlementAccountId)) {
      setSettlementAccountId(opts[0].id);
    }
  }, [paymentMethod, cashAccounts, bankAccounts, settlementAccountId]);

  const settlementOptions =
    paymentMethod === 'CASH'
      ? cashAccounts
      : paymentMethod === 'BANK'
        ? bankAccounts
        : [];

  const canSubmit =
    !submitting &&
    expenseAccountId !== '' &&
    netCents !== null &&
    netCents > 0 &&
    (paymentMethod === 'CREDIT' || settlementAccountId !== '');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!activeBusinessId) return;
    if (expenseAccountId === '') {
      setSubmitError('Choose what you paid for.');
      return;
    }
    if (netCents === null || netCents <= 0) {
      setSubmitError('Enter an amount greater than zero.');
      return;
    }
    if (paymentMethod !== 'CREDIT' && settlementAccountId === '') {
      setSubmitError('Choose the account the money came from.');
      return;
    }

    setSubmitting(true);
    try {
      const token = await getToken();
      const result = await createTransaction(token, {
        business_id: activeBusinessId,
        module: 'EXPENSE',
        transaction_date: txnDate,
        amount_cents: netCents,
        sale_category_id: null,
        income_account_id: null,
        expense_account_id: expenseAccountId,
        payment_method: paymentMethod,
        settlement_account_id: paymentMethod === 'CREDIT' ? null : settlementAccountId,
        tax_code_id: effectiveWht ? effectiveWht.id : null,
        tax_inclusive: false,
        quantity: null,
        counterparty: counterparty.trim() || null,
        description: null,
        reference: reference.trim() || null,
      });
      setLastResult(result);
      setAmount('');
      setWhtCodeId('');
      setReference('');
      setCounterparty('');
      await loadTransactions();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Could not record the payment.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppShell title="Expense / Payments">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* ---- Record a payment ---- */}
        <section className="lg:col-span-3">
          <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
            Record a payment
          </h2>

          {refsError && (
            <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300">
              {refsError}
            </p>
          )}

          {loadingRefs ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
          ) : !hasExpenseAccounts ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              This business has no expense accounts set up.
            </p>
          ) : (
            <form className={cardClass} onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className={labelClass} htmlFor="expense-account">
                    What did you pay for?
                  </label>
                  <select
                    id="expense-account"
                    className={inputClass}
                    value={expenseAccountId}
                    onChange={(e) => setExpenseAccountId(e.target.value)}
                  >
                    {cogsAccounts.length > 0 && (
                      <optgroup label="Cost of Goods Sold">
                        {cogsAccounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} {a.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {opexAccounts.length > 0 && (
                      <optgroup label="Operating Expenses">
                        {opexAccounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} {a.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Wages, depreciation and income tax are recorded in their own areas, not here.
                  </p>
                </div>

                <div>
                  <label className={labelClass} htmlFor="amount">
                    Amount ({currency})
                  </label>
                  <input
                    id="amount"
                    className={inputClass}
                    inputMode="decimal"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {paymentMethod === 'CREDIT'
                      ? 'The full amount of the bill.'
                      : 'What the supplier receives. Withholding, if any, is added below.'}
                  </p>
                </div>

                <div>
                  <label className={labelClass} htmlFor="date">
                    Date
                  </label>
                  <input
                    id="date"
                    type="date"
                    className={inputClass}
                    value={txnDate}
                    onChange={(e) => setTxnDate(e.target.value)}
                  />
                </div>

                <div className="sm:col-span-2">
                  <span className={labelClass}>How did you pay?</span>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {PAYMENT_OPTIONS.map((opt) => {
                      const selected = paymentMethod === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setPaymentMethod(opt.value)}
                          aria-pressed={selected}
                          className={
                            'rounded-md border px-3 py-2 text-sm font-medium transition ' +
                            (selected
                              ? 'border-brand-600 bg-brand-50 text-brand-700 dark:border-brand-400 dark:bg-brand-950 dark:text-brand-300'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800')
                          }
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {paymentMethod !== 'CREDIT' && (
                  <div className="sm:col-span-2">
                    <span className={labelClass}>
                      {paymentMethod === 'CASH'
                        ? 'From which cash account?'
                        : 'From which bank account?'}
                    </span>
                    {settlementOptions.length === 0 ? (
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        No {paymentMethod === 'CASH' ? 'cash' : 'bank'} account is set up for
                        this business.
                      </p>
                    ) : settlementOptions.length === 1 ? (
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                        {settlementOptions[0].code} {settlementOptions[0].name}
                      </div>
                    ) : (
                      <select
                        id="settlement-account"
                        className={inputClass}
                        value={settlementAccountId}
                        onChange={(e) => setSettlementAccountId(e.target.value)}
                      >
                        {settlementOptions.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} {a.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {paymentMethod === 'CREDIT'
                  ? hasWht && (
                      <div className="sm:col-span-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
                        Recording a bill on credit — withholding tax is applied when you pay it,
                        from the Cash / Bank card. The full amount is booked to Accounts Payable now.
                      </div>
                    )
                  : hasWht && (
                      <div className="sm:col-span-2">
                        <label className={labelClass} htmlFor="wht-code">
                          Withholding tax{' '}
                          <span className="font-normal text-slate-400">(optional)</span>
                        </label>
                        <select
                          id="wht-code"
                          className={inputClass}
                          value={whtCodeId}
                          onChange={(e) => setWhtCodeId(e.target.value)}
                        >
                          <option value="">None</option>
                          {whtResident.length > 0 && (
                            <optgroup label="Resident">
                              {whtResident.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {whtNonResident.length > 0 && (
                            <optgroup label="Non-resident">
                              {whtNonResident.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                        {effectiveWht && netCents !== null && netCents > 0 && (
                          <dl className="mt-2 space-y-1 rounded-md border border-slate-200 px-3 py-2 text-sm dark:border-slate-700">
                            <div className="flex justify-between">
                              <dt className="text-slate-500 dark:text-slate-400">Pays supplier</dt>
                              <dd className="tabular-nums text-slate-800 dark:text-slate-200">
                                {currency} {formatCents(netCents)}
                              </dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-slate-500 dark:text-slate-400">
                                Withholds to IRD ({effectiveWht.code})
                              </dt>
                              <dd className="tabular-nums text-slate-800 dark:text-slate-200">
                                {currency} {formatCents(whtCents)}
                              </dd>
                            </div>
                            <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold dark:border-slate-700">
                              <dt className="text-slate-700 dark:text-slate-300">Total expense</dt>
                              <dd className="tabular-nums text-slate-900 dark:text-slate-100">
                                {currency} {formatCents(grossCents)}
                              </dd>
                            </div>
                          </dl>
                        )}
                      </div>
                    )}

                <div>
                  <label className={labelClass} htmlFor="counterparty">
                    Supplier <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    id="counterparty"
                    className={inputClass}
                    placeholder="e.g. Island Wholesale"
                    value={counterparty}
                    onChange={(e) => setCounterparty(e.target.value)}
                  />
                </div>

                <div>
                  <label className={labelClass} htmlFor="reference">
                    Reference <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    id="reference"
                    className={inputClass}
                    placeholder="auto-generated"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                  />
                </div>
              </div>

              {submitError && (
                <p className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300">
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                className="mt-4 inline-flex items-center justify-center rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? 'Recording…' : 'Record payment'}
              </button>
            </form>
          )}

          {lastResult && (
            <ConfirmationPanel
              currency={currency}
              transaction={lastResult.transaction}
              entries={lastResult.entries}
              accountLabel={accountLabel}
              onDismiss={() => setLastResult(null)}
            />
          )}
        </section>

        {/* ---- Recent expenses ---- */}
        <section className="lg:col-span-2">
          <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
            Recent expenses
          </h2>
          <div className={cardClass}>
            {loadingList && transactions.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
            ) : transactions.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">No expenses recorded yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {transactions.map((t) => {
                  const gross = t.entries.reduce((s, e) => s + e.debit_cents, 0);
                  return (
                    <li key={t.id} className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                          {t.counterparty ?? t.description}
                        </p>
                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                          {t.transaction_date} · {t.reference ?? '—'}
                        </p>
                      </div>
                      <span className="shrink-0 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {currency} {formatCents(gross)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </div>
    </AppShell>
  );
}

// ---- confirmation panel ---------------------------------------------------

function ConfirmationPanel({
  currency,
  transaction,
  entries,
  accountLabel,
  onDismiss,
}: {
  currency: string;
  transaction: TransactionHeader;
  entries: TransactionEntry[];
  accountLabel: (id: string) => string;
  onDismiss: () => void;
}) {
  return (
    <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900 dark:bg-emerald-950">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
            Payment recorded
          </p>
          <p className="text-xs text-emerald-700 dark:text-emerald-400">
            Reference {transaction.reference ?? '—'} — the double entry we posted:
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="text-emerald-700 transition hover:text-emerald-900 dark:text-emerald-400 dark:hover:text-emerald-200"
        >
          ✕
        </button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-emerald-700/70 dark:text-emerald-400/70">
            <th className="py-1 font-medium">Account</th>
            <th className="py-1 text-right font-medium">Debit</th>
            <th className="py-1 text-right font-medium">Credit</th>
          </tr>
        </thead>
        <tbody className="text-slate-800 dark:text-slate-200">
          {entries.map((e) => (
            <tr key={e.id}>
              <td className="py-1">{accountLabel(e.account_id)}</td>
              <td className="py-1 text-right tabular-nums">
                {e.debit_cents ? `${currency} ${formatCents(e.debit_cents)}` : ''}
              </td>
              <td className="py-1 text-right tabular-nums">
                {e.credit_cents ? `${currency} ${formatCents(e.credit_cents)}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
